import type { ReactElement, RefObject } from 'react';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';
import debounce from 'lodash/debounce';

import { Box, xcss, Stack, Flex } from '@atlaskit/primitives';
import SearchIcon from '@atlaskit/icon/glyph/search';
import UploadIcon from '@atlaskit/icon/glyph/upload';
import { HelperMessage, Label } from '@atlaskit/form';
import Popup from '@atlaskit/popup';
import Textfield from '@atlaskit/textfield';
import Button from '@atlaskit/button/standard-button';
import type { Placement } from '@atlaskit/popper';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';

import { RateLimitedError, BadStatusError } from '@confluence/network';
import type { NetworkError } from '@confluence/network';

import { ConfigPanelSubheading } from '../ConfigPanelSubheading';
import { ImageSelectedComponent, UNSPLASH_URL } from '../ImageSelectedComponent';
import { AltTextField } from '../AltTextField';
import { FormFieldErrorMessage } from '../FormFieldErrorMessage';
import { useIsDisabledContext } from '../IsDisabledContext';
import type { UploadImageResult } from '../../link-cards/components/CardsAreaContainerComponents/LinkCardsUnsplashSearch';

import { DefaultImagePicker, renderImagePlaceholders } from './DefaultImagePicker';
import type { UnsplashResult } from './unsplashAPI';
import { getUnsplashPhotos } from './unsplashAPI';
import {
	UnsplashSearchContent,
	CARDS_DEFAULT_UNSPLASH_QUERY_WORD,
	PAGE_TITLE_DEFAULT_UNSPLASH_QUERY_WORD,
} from './UnsplashSearchContent';

export type UnsplashSearchProps = {
	sectionLabel: string | ReactElement;
	idPrefix: string;
	helperMessage?: string | ReactElement;
	id?: string;
	initialUnsplashResults?: UnsplashResult[] | undefined;
	handleImageSelection: ({ fullSizeImageUrl, altDescription }: UnsplashResult) => void;
	handleUploadedImageSelection?: ({ uploadedImageId, altDescription }: UploadImageResult) => void;
	imageAltText?: string;
	selectedImageUrl?: string;
	coverImageSrc?: string;
	openMediaBrowser?: () => void;
	placement?: Placement;
	dropdownPopupComponentRef?: RefObject<HTMLDivElement | null>;
	isDisabled?: boolean;
	contentId?: string;
	isExternalShare?: boolean;
	isPageTitleComponent?: boolean;
	analyticsSource: string;
	analyticsActionSubject: string;
	analyticsActionSubjectId: string;
	isUploadingImage?: boolean;
	allowUploadedImages?: boolean;
	shouldUseCompactStyles?: boolean;
	shouldShowAltTextField?: boolean;
	enableBottomSpacing?: boolean;
	shouldShowErrorMessage?: boolean;
};

const i18n = defineMessages({
	unsplashSearchPlaceholderText: {
		id: 'custom-sites-extensions.unsplash.search.placeholder',
		defaultMessage: 'Browse from Unsplash',
		description: 'Visible placeholder text for the text-field used to search for unsplash images',
	},
	unsplashLoadMoreText: {
		id: 'custom-sites-extensions.unsplash.search.load.more.unsplash.photos',
		defaultMessage: 'Load more',
		description: 'Text in button user can click to load more Unsplash images',
	},
	unsplashApiNoNetworkErrorMessage: {
		id: 'custom-sites-extensions.unsplash.search.error.no-network-message',
		defaultMessage: 'We‘ve lost the connection. Try again.',
		description:
			'Message for error shown to user when an api request made to Unsplash fails because the network appeared to be offline',
	},
	unsplashApiBadStatusErrorMessage: {
		id: 'custom-sites-extensions.unsplash.error.bad-request-message',
		defaultMessage: 'We can‘t get a response from Unsplash. Refresh and try again.',
		description:
			'Message for error shown to user when an api request made to Unsplash fails because the request was unsuccessful for reasons other than network or rate limit',
	},
	unsplashApiRateLimitedErrorMessage: {
		id: 'custom-sites-extensions.unsplash.error.rate-limited-message',
		defaultMessage: 'We can‘t get a response from Unsplash. Wait a moment, then try again.',
		description:
			'Message for error shown to user when an api request made to Unsplash fails because too many requests were sent to Unsplash in a given period',
	},
	unsplashSearchUploadMediaLabel: {
		id: 'custom-sites-extensions.unsplash.upload.label',
		defaultMessage: 'Upload',
		description:
			'Opens a dialog to be able to select an image from a users computer to upload and use',
	},
	uploadedImageUnavailableMessage: {
		id: 'custom-sites-extensions.unsplash.image.upload.not.available',
		defaultMessage:
			'Your current image isn‘t available at the moment. Select a new image to replace it.',
		description: 'Message when a user has an uploaded image but the image upload FF is turned off',
	},
});

enum UnsplashFetchError {
	NO_NETWORK_ERROR = 'network_error',
	RATE_LIMITED_ERROR = 'rate_limited_error',
	BAD_STATUS_ERROR = 'bad_status_error',
}

const ImagesWrapperStyles = xcss({
	marginTop: 'space.100',
	justifyContent: 'space-between',
	paddingTop: 'space.0',
	paddingRight: 'space.025',
	paddingLeft: 'space.025',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnsplashSearchFlexContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UploadButtonWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> button': {
		width: '28px',
		padding: token('space.0', '0px'),
		height: '24px',
		marginRight: token('space.050', '4px'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> button svg': {
		width: '20px',
		height: '20px',
	},
});

const bottomSpacing = xcss({ marginBottom: 'space.100' });
const removeBottomSpacing = xcss({ marginBottom: 'space.0' });

const i18nErrorText = {
	[UnsplashFetchError.NO_NETWORK_ERROR]: (
		<FormattedMessage {...i18n.unsplashApiNoNetworkErrorMessage} />
	),
	[UnsplashFetchError.BAD_STATUS_ERROR]: (
		<FormattedMessage {...i18n.unsplashApiBadStatusErrorMessage} />
	),
	[UnsplashFetchError.RATE_LIMITED_ERROR]: (
		<FormattedMessage {...i18n.unsplashApiRateLimitedErrorMessage} />
	),
};

const iconContainerStyles = xcss({
	paddingLeft: 'space.100',
});

const helperMessageWrapperStyles = xcss({
	marginBottom: 'space.100',
	display: 'flex',
	justifyContent: 'space-between',
});

const helperMessageDisabledStyles = xcss({
	color: 'color.text.disabled',
});

const uploadedImageUnavailableStyles = xcss({
	marginTop: 'space.050',
});

export const UnsplashSearch: React.FC<UnsplashSearchProps> = ({
	id,
	idPrefix,
	imageAltText,
	sectionLabel,
	helperMessage,
	selectedImageUrl,
	initialUnsplashResults,
	coverImageSrc,
	handleImageSelection,
	handleUploadedImageSelection,
	openMediaBrowser,
	dropdownPopupComponentRef,
	placement = 'bottom',
	isExternalShare,
	contentId,
	isPageTitleComponent,
	analyticsSource,
	analyticsActionSubject,
	analyticsActionSubjectId,
	isUploadingImage,
	allowUploadedImages = true,
	shouldUseCompactStyles = false,
	shouldShowAltTextField = false,
	enableBottomSpacing = true,
	shouldShowErrorMessage = false,
}) => {
	const intl = useIntl();
	const isDisabled = useIsDisabledContext();

	const [defaultUnsplashResults, setDefaultUnsplashResults] = useState<
		UnsplashResult[] | undefined
	>([]);

	const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);

	const [unSplashSearchResults, setUnsplashSearchResults] = useState<UnsplashResult[] | undefined>(
		[],
	);
	const [error, setError] = useState<UnsplashFetchError | undefined>();

	const [unsplashResultsCountFromLastAPICall, setUnsplashResultsCountFromLastAPICall] =
		useState<number>(30);

	const searchResultsRef = useRef<HTMLDivElement>(null);

	const pageNumber = useRef<number>(1);

	const textfieldInputValue = useRef<string>('');

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const shouldShowUserImageSelectedComponent =
		!!contentId &&
		selectedImageUrl &&
		!selectedImageUrl?.includes(UNSPLASH_URL) &&
		allowUploadedImages;

	const isUploadedImageUnavailable =
		!allowUploadedImages && !!selectedImageUrl && !selectedImageUrl?.includes(UNSPLASH_URL);

	const unsplashSearchFieldId = id
		? `${idPrefix}-search-unsplash-${id}`
		: `${idPrefix}-search-unsplash`;

	const handleError = useCallback(
		(error: NetworkError) => {
			if (error instanceof RateLimitedError) {
				setError(UnsplashFetchError.RATE_LIMITED_ERROR);
			} else if (error instanceof BadStatusError) {
				setError(UnsplashFetchError.BAD_STATUS_ERROR);
			} else {
				setError(UnsplashFetchError.NO_NETWORK_ERROR);
			}
			setUnsplashSearchResults(undefined);
		},
		[setError, setUnsplashSearchResults],
	);

	const debouncedQuery = debounce((searchBar) => {
		textfieldInputValue.current = searchBar?.value;
		const defaultQueryWord = isPageTitleComponent
			? PAGE_TITLE_DEFAULT_UNSPLASH_QUERY_WORD
			: CARDS_DEFAULT_UNSPLASH_QUERY_WORD;
		const queryKeyword = textfieldInputValue.current || defaultQueryWord;
		setUnsplashSearchResults(Array(30).fill(null));

		void getUnsplashPhotos(queryKeyword, pageNumber.current, isPageTitleComponent)
			.then((result) => {
				if (!defaultUnsplashResults || defaultUnsplashResults.length === 0) {
					setDefaultUnsplashResults(result);
				}
				setUnsplashResultsCountFromLastAPICall(result.length);
				setUnsplashSearchResults(result);
				setError(undefined);
			})
			.catch((error) => {
				handleError(error);
			});

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'typed',
				actionSubject: 'headerImageSearch',
				actionSubjectId: 'headerImageSearchTyped',
				source: analyticsSource,
			},
		}).fire();
	}, 350);

	const handleSearchOnChange = (event: React.FormEvent<HTMLInputElement>) => {
		pageNumber.current = 1;
		debouncedQuery(event.target);
		searchResultsRef.current?.scroll({ top: 0, behavior: 'smooth' });
	};

	const handleChange = () => {
		setIsPopupOpen(true);
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (['Enter', ' ', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
			if (event.key === ' ' && !textfieldInputValue.current) {
				event.preventDefault();
			}
			handleChange();
		}
	};

	const handleUploadButtonClick = () => {
		openMediaBrowser && openMediaBrowser();

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: analyticsActionSubject,
				actionSubjectId: analyticsActionSubjectId,
				source: analyticsSource,
			},
		}).fire();
	};

	useEffect(() => {
		if (!initialUnsplashResults) {
			const defaultUnsplashQueryWord = isPageTitleComponent
				? PAGE_TITLE_DEFAULT_UNSPLASH_QUERY_WORD
				: CARDS_DEFAULT_UNSPLASH_QUERY_WORD;
			void getUnsplashPhotos(defaultUnsplashQueryWord, 1, isPageTitleComponent)
				.then((result) => {
					setUnsplashSearchResults(result);
					setDefaultUnsplashResults(result);
				})
				.catch((error) => {
					handleError(error);
				});
		} else {
			setUnsplashSearchResults(initialUnsplashResults);
			setDefaultUnsplashResults(initialUnsplashResults);
		}
	}, [initialUnsplashResults, isPageTitleComponent, handleError]);

	const renderErrorState = () => {
		return (
			<Stack>
				<Flex xcss={ImagesWrapperStyles} testId="default-image-picker">
					{renderImagePlaceholders()}
				</Flex>
				<FormFieldErrorMessage>{i18nErrorText[error!]}</FormFieldErrorMessage>
			</Stack>
		);
	};

	return (
		<UnsplashSearchFlexContainer>
			<Label htmlFor={unsplashSearchFieldId}>
				<ConfigPanelSubheading>{sectionLabel}</ConfigPanelSubheading>
			</Label>
			{helperMessage && (
				<Box xcss={helperMessageWrapperStyles}>
					<HelperMessage>
						<Box xcss={isDisabled ? helperMessageDisabledStyles : undefined}>{helperMessage}</Box>
					</HelperMessage>
				</Box>
			)}
			<Popup
				/* eslint-disable jsx-a11y/no-autofocus */
				autoFocus={false}
				onClose={() => setIsPopupOpen(false)}
				content={() => (
					<UnsplashSearchContent
						dropdownPopupComponentRef={dropdownPopupComponentRef}
						unSplashSearchResults={unSplashSearchResults}
						idPrefix={idPrefix}
						handleImageSelection={handleImageSelection}
						setIsPopupOpen={setIsPopupOpen}
						loadMoreText={i18n.unsplashLoadMoreText}
						queryWord={textfieldInputValue}
						pageNumber={pageNumber}
						setUnsplashSearchResults={setUnsplashSearchResults}
						handleError={handleError}
						searchResultsRef={searchResultsRef}
						unsplashResultsCountFromLastAPICall={unsplashResultsCountFromLastAPICall}
						setUnsplashResultsCountFromLastAPICall={setUnsplashResultsCountFromLastAPICall}
						isPageTitleComponent={isPageTitleComponent}
						analyticsSource={analyticsSource}
					/>
				)}
				isOpen={isPopupOpen}
				placement={placement}
				testId="unsplash-popup"
				trigger={(triggerProps) => (
					<Textfield
						{...triggerProps}
						id={unsplashSearchFieldId}
						testId={`${idPrefix}-unsplash-search-bar`}
						onChange={handleSearchOnChange}
						onClick={() => handleChange()}
						onInput={() => handleChange()}
						onKeyDown={(event) => handleKeyDown(event)}
						isDisabled={isUploadingImage || isDisabled}
						elemBeforeInput={
							<Box xcss={iconContainerStyles}>
								<SearchIcon size="small" label="search" />
							</Box>
						}
						elemAfterInput={
							!!openMediaBrowser ? (
								<UploadButtonWrapper>
									<Button
										onClick={handleUploadButtonClick}
										appearance="subtle"
										testId="open-media-browser"
										isDisabled={isUploadingImage || isDisabled}
									>
										<UploadIcon
											size="medium"
											label={intl.formatMessage(i18n.unsplashSearchUploadMediaLabel)}
										/>
									</Button>
								</UploadButtonWrapper>
							) : null
						}
						placeholder={intl.formatMessage(i18n.unsplashSearchPlaceholderText)}
					/>
				)}
			/>
			{error ? (
				renderErrorState()
			) : (
				<Box xcss={enableBottomSpacing ? bottomSpacing : removeBottomSpacing}>
					<DefaultImagePicker
						initialUnsplashResults={defaultUnsplashResults}
						handleImageSelection={handleImageSelection}
						imageAltText={imageAltText}
						selectedImageUrl={
							coverImageSrc !== selectedImageUrl && selectedImageUrl?.includes(UNSPLASH_URL)
								? selectedImageUrl
								: ''
						}
						analyticsSource={analyticsSource}
						isUploadingImage={isUploadingImage}
					/>
					{shouldShowUserImageSelectedComponent ? (
						<>
							<ImageSelectedComponent
								contentId={contentId}
								selectedImageId={selectedImageUrl}
								isExternalShare={isExternalShare || false}
								shouldUseCompactStyles={shouldUseCompactStyles}
								shouldShowErrorMessage={shouldShowErrorMessage}
							/>
							{shouldShowAltTextField && (
								<AltTextField
									imageAltText={imageAltText}
									selectedImageUrl={selectedImageUrl}
									handleImageSelection={
										handleUploadedImageSelection as ({
											uploadedImageId,
											altDescription,
										}: UploadImageResult) => void
									}
									intl={intl}
									isDisabled={isDisabled}
								/>
							)}
						</>
					) : null}
				</Box>
			)}
			{isUploadedImageUnavailable && (
				<Box xcss={uploadedImageUnavailableStyles}>
					<FormFieldErrorMessage>
						<FormattedMessage {...i18n.uploadedImageUnavailableMessage} />
					</FormFieldErrorMessage>
				</Box>
			)}
		</UnsplashSearchFlexContainer>
	);
};
